import { useState, useEffect } from 'react';
import { Card, Button, Input } from '@atoms/index';
import { cn } from '@/utils/cn';

interface WorkingHoursEntry {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isClosed: boolean;
}

const DAYS = [
  { value: 0, label: 'Pazar' },
  { value: 1, label: 'Pazartesi' },
  { value: 2, label: 'Salı' },
  { value: 3, label: 'Çarşamba' },
  { value: 4, label: 'Perşembe' },
  { value: 5, label: 'Cuma' },
  { value: 6, label: 'Cumartesi' },
];

interface OperatingHoursFormProps {
  initialHours?: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }>;
  onSave: (hours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>) => Promise<void>;
  onCancel?: () => void;
}

export function OperatingHoursForm({
  initialHours = [],
  onSave,
  onCancel,
}: OperatingHoursFormProps) {
  const buildHoursFromInitial = (source: Array<{ dayOfWeek: number; startTime: string; endTime: string }>) =>
    DAYS.map((day) => {
      const existing = source.find((h) => h.dayOfWeek === day.value);
      return {
        dayOfWeek: day.value,
        startTime: existing?.startTime || '09:00',
        endTime: existing?.endTime || '22:00',
        isClosed: !existing,
      };
    });

  const [hours, setHours] = useState<WorkingHoursEntry[]>(() => buildHoursFromInitial(initialHours));

  const initialHoursKey = initialHours.length ? initialHours.map((h) => `${h.dayOfWeek}:${h.startTime}-${h.endTime}`).join('|') : 'empty';
  useEffect(() => {
    setHours(buildHoursFromInitial(initialHours));
  }, [initialHoursKey]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDayChange = (dayOfWeek: number, field: 'startTime' | 'endTime' | 'isClosed', value: string | boolean) => {
    setHours((prev) =>
      prev.map((h) =>
        h.dayOfWeek === dayOfWeek
          ? { ...h, [field]: value }
          : h,
      ),
    );
  };

  const handleSave = async () => {
    const validHours = hours
      .filter((h) => !h.isClosed)
      .map((h) => ({
        dayOfWeek: h.dayOfWeek,
        startTime: h.startTime,
        endTime: h.endTime,
      }));

    setIsSubmitting(true);
    try {
      await onSave(validHours);
    } catch (error) {
      console.error('Failed to save working hours:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyToAll = (sourceDay: number) => {
    const source = hours.find((h) => h.dayOfWeek === sourceDay);
    if (!source) return;

    setHours((prev) =>
      prev.map((h) => ({
        ...h,
        startTime: source.startTime,
        endTime: source.endTime,
        isClosed: source.isClosed,
      })),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end mb-4">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleCopyToAll(1)}
          >
            Pazartesi'yi Tümüne Kopyala
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {DAYS.map((day) => {
          const dayHours = hours.find((h) => h.dayOfWeek === day.value);
          if (!dayHours) return null;

          return (
            <Card key={day.value} className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-24">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!dayHours.isClosed}
                      onChange={(e) =>
                        handleDayChange(day.value, 'isClosed', !e.target.checked)
                      }
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="font-medium text-gray-900">{day.label}</span>
                  </label>
                </div>

                {!dayHours.isClosed && (
                  <>
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        type="time"
                        value={dayHours.startTime}
                        onChange={(e) =>
                          handleDayChange(day.value, 'startTime', e.target.value)
                        }
                        className="flex-1"
                      />
                      <span className="text-gray-500">-</span>
                      <Input
                        type="time"
                        value={dayHours.endTime}
                        onChange={(e) =>
                          handleDayChange(day.value, 'endTime', e.target.value)
                        }
                        className="flex-1"
                      />
                    </div>
                  </>
                )}

                {dayHours.isClosed && (
                  <div className="flex-1">
                    <span className="text-gray-500 italic">Kapalı</span>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex gap-3 pt-4 border-t justify-end">
        {onCancel && (
          <Button variant="secondary" size="sm" onClick={onCancel}>
            İptal
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          isLoading={isSubmitting}
        >
          Kaydet
        </Button>
      </div>
    </div>
  );
}
